import React, { useEffect, useState, useRef } from 'react';
import './FileManager.css';
import * as api from 'api.js';
import { useGlobalState } from 'hooks';
import { Modal, Form } from 'react-bootstrap';

export const FileUploadModal = ({ show, setShow }) => {
    const [state, dispatch] = useGlobalState();

    const [validationEnabled, setValidationEnabled] = useState(false);
    const [description, setDescription] = useState('');
    const [info, setInfo] = useState('');
    const [eventTargetFile, setEventTargetFile] = useState(null);
    const [formData, setFormData] = useState(null);

    const formRef = useRef(null);

    const uploadFolder = formatFolder(state.currentPath.join('/'));

    useEffect(() => {
        if (!formData) return;
        dispatch.setBusy(true);
        const uploadFile = async () => {
            try {
                const response = await api.uploadFile(formData);
                cleanUp();
                dispatch.refreshFileBrowser();
            } catch (error) {
                if (error.response) {
                    console.log(JSON.stringify(error.response));
                }
            } finally {
                dispatch.setBusy(false);
            }
        };
        uploadFile();
    }, [formData]);

    const cleanUp = () => {
        formRef.current.reset();
        setEventTargetFile(null);
        setInfo('');
        setDescription('');
        setValidationEnabled(false);
        setFormData(null);
        setShow(false);
    };

    const handleChange = (event) => {
        const file = event.target.files[0];

        if (file.size > 5000) {
            event.target.value = '';
            return alert(`
                Binary file maximum size is 5 kbytes.
                Size of your file was ${Math.trunc(file.size / 1000)} kilobytes.
            `);
        }

        if (state.currentItems.find((item) => item.name === file.name) !== undefined) {
            event.target.value = '';
            return alert(`
                This folder already contains ${file.name}.
                Folder can not contain identically named files.
            `);
        }

        setEventTargetFile(file);
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        /*
        const form = event.currentTarget;
        if (!form.checkValidity()) return;
        */

        if (!eventTargetFile) {
            setValidationEnabled(true);
            return;
        }

        const _formData = new FormData();

        _formData.append('file', eventTargetFile);
        _formData.append(
            'metadata',
            JSON.stringify({
                description: description,
                additional_info: info,
            })
        );
        _formData.append('prefix', uploadFolder);
        setFormData(_formData);
    };

    return (
        <div>
            <Modal show={show}>
                <Modal.Header>Add new binary to be uploaded to {uploadFolder}</Modal.Header>
                <Modal.Body>
                    <Form ref={formRef} id="binary-upload-form" noValidate onSubmit={handleSubmit}>
                        <Form.Group>
                            <Form.Control
                                type="file"
                                name="file"
                                onChange={handleChange}
                                isInvalid={validationEnabled && !eventTargetFile}
                            />
                            <Form.Control.Feedback type="invalid">
                                Add binary file
                            </Form.Control.Feedback>
                            <Form.Text>Binary file maximum size is 5 kbytes</Form.Text>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={description}
                                onChange={(event) => setDescription(event.target.value)}
                                maxLength="100"
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Additional info</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={info}
                                onChange={(event) => setInfo(event.target.value)}
                                maxLength="255"
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <button className="styled_button" onClick={cleanUp}>
                        Cancel
                    </button>
                    <button className="styled_button" type="submit" form="binary-upload-form">
                        Upload file
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export const FolderCreateModal = ({ show, setShow, type = 'vendor' }) => {
    const [state, dispatch] = useGlobalState();

    const [validationEnabled, setValidationEnabled] = useState(false);
    const [newFolder, setNewFolder] = useState('');
    const [formData, setFormData] = useState(null);
    const [notUnique, setNotUnique] = useState(false);
    const [valueMissing, setValueMissing] = useState(true);

    const formRef = useRef(null);

    // do not try to join if creating folder to root:
    const uploadFolder =
        state.currentPath.length === 0 ? '' : formatFolder(state.currentPath.join('/'));
    //const uploadFolder = formatFolder(state.currentPath.join('/'));

    useEffect(() => {
        if (!formData) return;
        dispatch.setBusy(true);
        const uploadFile = async () => {
            try {
                const response = await api.createFolder(formData);
                cleanUp();
                dispatch.refreshFileBrowser();
            } catch (error) {
                if (error.response) {
                    console.log(JSON.stringify(error.response));
                }
            } finally {
                dispatch.setBusy(false);
            }
        };
        uploadFile();
    }, [formData]);

    const cleanUp = () => {
        formRef.current.reset();
        setNewFolder('');
        setValidationEnabled(false);
        setFormData(null);
        setShow(false);
        setValueMissing(true);
        setNotUnique(false);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setValueMissing(false);
        setNotUnique(false);

        let trimmed = newFolder.trim();

        if (trimmed.length === 0) {
            setValueMissing(true);
            setValidationEnabled(true);
            return;
        }

        if (state.currentItems.find((item) => item === newFolder.trim()) !== undefined) {
            setNotUnique(true);
            setValidationEnabled(true);
            return;
        }

        setFormData(JSON.stringify({ folder: uploadFolder + newFolder.trim() }));
    };

    return (
        <div>
            <Modal show={show}>
                <Modal.Header>
                    Add new {type} to {uploadFolder}
                </Modal.Header>
                <Modal.Body>
                    <Form ref={formRef} id="folder-create-form" noValidate onSubmit={handleSubmit}>
                        <Form.Group>
                            <Form.Label>{capitalizeFirstLetter(type)}:</Form.Label>
                            <Form.Control
                                value={newFolder}
                                onChange={(event) => setNewFolder(event.target.value)}
                                maxLength="30"
                                isInvalid={validationEnabled && (notUnique || valueMissing)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {notUnique && (
                                    <span>{capitalizeFirstLetter(type)} already exists</span>
                                )}
                                {valueMissing && <span>Add {type}</span>}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <button className="styled_button" onClick={cleanUp}>
                        Cancel
                    </button>
                    <button className="styled_button" type="submit" form="folder-create-form">
                        Add {type}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export const FileEditModal = ({ show, setShow }) => {
    const [state, dispatch] = useGlobalState();

    const [validationEnabled, setValidationEnabled] = useState(false);
    const [description, setDescription] = useState(state.selectedFile.metadata.description);
    const [info, setInfo] = useState(state.selectedFile.metadata.additional_info);
    const [formData, setFormData] = useState(null);

    const formRef = useRef(null);

    const uploadFolder = formatFolder(state.currentPath.join('/'));

    useEffect(() => {
        setInfo(state.selectedFile.metadata.additional_info);
        setDescription(state.selectedFile.metadata.description);
    }, [state.selectedFile.metadata.additional_info, state.selectedFile.metadata.description]);

    useEffect(() => {
        if (!formData) return;
        dispatch.setBusy(true);
        const editFile = async () => {
            try {
                dispatch.setRememberSelectedFile(true);
                const response = await api.uploadFile(formData);
                cleanUp();                
                dispatch.setSelectedFile({
                    name: state.selectedFile.name,
                    size: state.selectedFile.size,
                    modified: response.LastModified,
                    metadata: { description: description, additional_info: info },
                });        
                dispatch.refreshFileBrowser();        
            } catch (error) {
                if (error.response) {
                    console.log(JSON.stringify(error.response));
                }
            } finally {
                dispatch.setBusy(false);
            }
        };
        editFile();
    }, [formData]);

    const cleanUp = () => {
        formRef.current.reset();
        setInfo(state.selectedFile.metadata.additional_info);
        setDescription(state.selectedFile.metadata.description);
        setValidationEnabled(false);
        setFormData(null);
        setShow(false);
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        /*
        const form = event.currentTarget;
        if (!form.checkValidity()) return;
        */

        const _formData = new FormData();

        //_formData.append('file', eventTargetFile);
        _formData.append(
            'metadata',
            JSON.stringify({
                description: description,
                additional_info: info,
            })
        );
        _formData.append('prefix', uploadFolder);
        _formData.append('filename', state.selectedFile.name);
        _formData.append('update_metadata', true);
        setFormData(_formData);
    };

    return (
        <div>
            <Modal show={show}>
                <Modal.Header>
                    <span>Edit <strong>{state.selectedFile.name}</strong> metadata</span>
                </Modal.Header>
                <Modal.Body>
                    <Form ref={formRef} id="binary-edit-form" noValidate onSubmit={handleSubmit}>
                        <Form.Group>
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={description}
                                onChange={(event) => setDescription(event.target.value)}
                                maxLength="100"
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Additional info</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={info}
                                onChange={(event) => setInfo(event.target.value)}
                                maxLength="255"
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <button className="styled_button" onClick={cleanUp}>
                        Cancel
                    </button>
                    <button className="styled_button" type="submit" form="binary-edit-form">
                        Save changes
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

function formatFolder(folder) {
    if (folder.match(/.*\/$/)) {
        return folder;
    }
    return folder + '/';
}

function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}
